/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckOutlined, DownOutlined, SearchOutlined } from '@ant-design/icons';

const MENU_MAX_HEIGHT = 280;
const MENU_GAP = 4;

/**
 * Light custom select — Lessons Learned SingleSelect sister.
 * Avoids native/OS dark menus and global antd select skins.
 * Menu renders in a portal so modals (overflow:hidden) do not clip it.
 */
const AdminAiSelect = ({
  label,
  options = [],
  value = '',
  onChange,
  placeholder = 'Select…',
  disabled = false,
  searchable = true,
  className = '',
  menuPortal = true,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const listId = useId();

  const normalized = useMemo(
    () =>
      options.map((o) =>
        typeof o === 'string'
          ? { value: o, label: o }
          : { value: String(o.value), label: String(o.label ?? o.value) }
      ),
    [options]
  );

  const selected = normalized.find((o) => o.value === String(value));
  const display = selected?.label || '';

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return normalized;
    return normalized.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }, [normalized, query]);

  const updateMenuPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const spaceBelow = viewportH - rect.bottom - MENU_GAP;
    const spaceAbove = rect.top - MENU_GAP;
    const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(
      MENU_MAX_HEIGHT,
      Math.max(120, openUp ? spaceAbove - 8 : spaceBelow - 8)
    );
    const top = openUp ? rect.top - MENU_GAP - maxHeight : rect.bottom + MENU_GAP;
    setMenuStyle({
      top: Math.max(8, top),
      left: rect.left,
      width: rect.width,
      maxHeight,
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (open && searchable) {
      const t = window.setTimeout(() => searchRef.current?.focus(), 40);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open, searchable]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
      setQuery('');
    }
  }, [disabled]);

  const pick = (next) => {
    onChange?.(next);
    setOpen(false);
    setQuery('');
  };

  const menu = open ? (
    <div
      ref={menuRef}
      className={`admin-ai-select-menu${menuPortal ? ' admin-ai-select-menu--portal' : ''}`}
      role="listbox"
      id={listId}
      style={
        menuPortal && menuStyle
          ? {
              top: menuStyle.top,
              left: menuStyle.left,
              width: menuStyle.width,
            }
          : undefined
      }
    >
      {searchable && normalized.length > 6 ? (
        <div className="admin-ai-select-search">
          <SearchOutlined />
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            aria-label="Search options"
          />
        </div>
      ) : null}
      <div
        className="admin-ai-select-list"
        style={menuPortal && menuStyle ? { maxHeight: menuStyle.maxHeight } : undefined}
      >
        {filtered.length === 0 ? (
          <div className="admin-ai-select-empty">No matches</div>
        ) : (
          filtered.map((o) => {
            const active = o.value === String(value);
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={active}
                className={`admin-ai-select-option${active ? ' is-active' : ''}`}
                onClick={() => pick(o.value)}
                title={o.label}
              >
                <span>{o.label}</span>
                {active ? <CheckOutlined /> : null}
              </button>
            );
          })
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className={`admin-ai-select-wrap ${className}`.trim()} ref={rootRef}>
      {label ? <span className="admin-ai-label">{label}</span> : null}
      <button
        ref={triggerRef}
        type="button"
        className={`admin-ai-select-trigger${open ? ' is-open' : ''}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => !disabled && setOpen((v) => !v)}
      >
        <span className={`admin-ai-select-value${display ? '' : ' is-placeholder'}`}>
          {display || placeholder}
        </span>
        <DownOutlined className="admin-ai-select-caret" />
      </button>

      {menuPortal && menu ? createPortal(menu, document.body) : menu}
    </div>
  );
};

export default AdminAiSelect;
